import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  useRefunds,
  REFUND_STATUS_LABELS,
  type RefundReferenceKind,
} from "@/lib/billing/useRefunds";
import { Receipt } from "lucide-react";

const REF_LABELS: Record<RefundReferenceKind, string> = {
  alacarte: "À la carte purchase",
  subscription: "Subscription / tier",
  other: "Something else",
};

const fmt = (iso: string) => new Date(iso).toLocaleDateString();

const RefundsPanel = () => {
  const { refunds, loading, submit } = useRefunds();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refKind, setRefKind] = useState<RefundReferenceKind>("alacarte");
  const [refId, setRefId] = useState("");
  const [reason, setReason] = useState("");

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error("Tell us what happened.");
      return;
    }
    setBusy(true);
    const { error } = await submit({
      reference_kind: refKind,
      reference_id: refId.trim() || undefined,
      reason: reason.trim(),
    });
    setBusy(false);
    if (error) {
      toast.error(error);
      return;
    }
    setOpen(false);
    setRefId("");
    setReason("");
    toast.success("Refund request submitted. We'll review within 5 business days.");
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Wavlynq honors refund requests on a case-by-case basis within 14 days of
        purchase. Subscription refunds are prorated.
      </p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline">
            <Receipt className="h-4 w-4 mr-2" />
            Request a refund
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request a refund</DialogTitle>
            <DialogDescription>
              We review every refund manually. You'll hear back within 5 business days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>What's the refund for?</Label>
              <Select value={refKind} onValueChange={(v) => setRefKind(v as RefundReferenceKind)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(REF_LABELS) as RefundReferenceKind[]).map((k) => (
                    <SelectItem key={k} value={k}>{REF_LABELS[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="rf-id">Reference ID (if you have one)</Label>
              <Input
                id="rf-id"
                value={refId}
                placeholder="e.g. order ID or Stripe charge id"
                maxLength={200}
                onChange={(e) => setRefId(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="rf-reason">What happened?</Label>
              <Textarea
                id="rf-reason"
                value={reason}
                rows={4}
                maxLength={1000}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={busy}>
              {busy ? "Submitting…" : "Submit"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {refunds.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Your refund requests
          </p>
          {refunds.map((r) => (
            <div key={r.id} className="rounded-lg border border-border bg-card px-4 py-2.5 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">{REF_LABELS[r.reference_kind]}</p>
                <span className={`text-xs ${
                  r.status === "processed" ? "text-success"
                    : r.status === "denied" ? "text-destructive"
                    : "text-muted-foreground"
                }`}>
                  {REFUND_STATUS_LABELS[r.status]}
                </span>
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">{r.reason}</p>
              <p className="text-xs text-muted-foreground">{fmt(r.created_at)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RefundsPanel;
